/**
 * Mapping utilities for converting between web domain types (Conversation, History)
 * and UI contract types (ChatMessage, ChatHistory) from @f2e/ui.
 *
 * Use these functions at the boundary between web app code and @f2e/ui components
 * to maintain type safety and avoid `as` type assertions.
 *
 * Note: We use type assertions for enum types (Role/MessageRole, ReasoningStepType)
 * because they are structurally identical but defined in different modules.
 */
import type {
  ChatMessage,
  ChatMessageGroup,
  ChatMessageMeta,
  ChatHistory,
  MessageRole,
  ReasoningStep as UIReasoningStep,
} from "@f2e/ui/components/Chat"
import type {
  FormRequestSchema,
  FormRequestField,
} from "@f2e/ui/components/InteractionRequestForm"
import type { InteractionRequestData } from "@f2e/ui/chat/transport"
import { normalizeReasoningStep } from "@f2e/shared/utils"

import type {
  Conversation,
  ConversationGroup,
  ConversationMeta,
  History,
  ReasoningStep,
  ConversationFeedback,
  FeedbackSystemType,
  FileBadgeData,
  AgentType,
  Identity,
} from "@/types"
import { Role, FileAttachmentType } from "@/types"
type HitlFormRequestSchema = Extract<
  InteractionRequestData,
  { interactionType: "form" }
>["formSchema"]
type HitlFormRequestField = HitlFormRequestSchema["fields"][number]

// ----- Feedback helpers -----

export interface WebChatMapperContext {
  /**
   * Viewer identifier used to derive per-user UI states.
   *
   * If omitted, we fall back to legacy behavior (any feedback locks the UI).
   */
  viewerIdentity?: Identity
}

/**
 * Check if feedbacks array contains a specific system feedback type.
 */
const hasFeedbackType = (
  feedbacks: ConversationFeedback[] | undefined,
  type: FeedbackSystemType,
  viewerIdentity?: Identity
): boolean => {
  if (!feedbacks) return false

  if (viewerIdentity?.externalUserId) {
    return feedbacks.some(
      (fb) =>
        fb.tag === "system" &&
        fb.type === type &&
        fb.identity.externalUserId === viewerIdentity.externalUserId
    )
  }

  if (viewerIdentity?.userId !== undefined && viewerIdentity?.userId !== null) {
    return feedbacks.some(
      (fb) =>
        fb.tag === "system" &&
        fb.type === type &&
        fb.identity.userId === viewerIdentity.userId
    )
  }

  // Legacy / unknown viewer fallback:
  // If we don't know who the viewer is, treat any feedback as "submitted".
  return feedbacks.some((fb) => fb.tag === "system" && fb.type === type)
}

/**
 * Derive helpfulSubmitted from conversation feedbacks.
 * Returns true if a sys_helpful feedback exists for the viewer.
 */
const deriveHelpfulSubmitted = (
  feedbacks: ConversationFeedback[] | undefined,
  viewerIdentity?: Identity
): boolean => hasFeedbackType(feedbacks, "sys_helpful", viewerIdentity)

/**
 * Derive improveSubmitted from conversation feedbacks.
 * Returns true if a sys_improve feedback exists for the viewer.
 */
const deriveImproveSubmitted = (
  feedbacks: ConversationFeedback[] | undefined,
  viewerIdentity?: Identity
): boolean => hasFeedbackType(feedbacks, "sys_improve", viewerIdentity)

// ----- Type converters -----

/**
 * Convert app ReasoningStep to UI ReasoningStep.
 */
const toUIReasoningStep = (step: ReasoningStep): UIReasoningStep =>
  normalizeReasoningStep(step) as unknown as UIReasoningStep

/**
 * Decide whether a domain attachment should be shown in the UI.
 *
 * We intentionally hide internal/system-only attachments (e.g. page snapshots)
 * from the chat UI while keeping them available in the domain model.
 */
const shouldShowAttachmentInUI = (file: FileBadgeData): boolean => {
  // Prefer structured type when available.
  if (file.attachmentType === FileAttachmentType.PageSnapshot) return false

  // Backward-compat / safety net: some older payloads might not include attachmentType.
  if (file.name === "page_snapshot.xml") return false

  return true
}

/**
 * Convert app ConversationMeta to UI ChatMessageMeta.
 */
const toUIMeta = (meta?: ConversationMeta): ChatMessageMeta | undefined => {
  if (!meta) return undefined
  return {
    reasoningSteps: meta.reasoningSteps?.map(toUIReasoningStep),
    relatedQuestions: meta.relatedQuestions,
    agentProfile: meta.agentProfile
      ? {
          id: meta.agentProfile.id,
          name: meta.agentProfile.name,
          description: undefined,
        }
      : undefined,
    assistantResponse: meta.assistantResponse,
    outputComponents: meta.outputComponents,
  }
}

/**
 * Convert app Conversation to UI ChatMessage.
 * Use when passing data to @f2e/ui components.
 *
 * Derives helpfulSubmitted/improveSubmitted from conversation.feedbacks.
 *
 * - If `ctx.viewerIdentity` is provided: derive per-user submitted states
 *   by matching `feedback.identity.externalUserId`.
 * - Otherwise: fallback to legacy behavior (any feedback locks the UI).
 */
export const toUIMessage = (
  conversation: Conversation,
  ctx?: WebChatMapperContext
): ChatMessage => {
  const visibleAttachments = conversation.attachedFiles?.filter(
    shouldShowAttachmentInUI
  )

  return {
    id: conversation.id,
    groupId: conversation.groupId,
    role: conversation.role as MessageRole,
    content: conversation.content,
    externalUserId:
      conversation.identity?.externalUserId ||
      (conversation.role === Role.User
        ? conversation.identity?.userEmail
        : undefined),
    agentId: undefined, // web Conversation doesn't have agentId at message level
    attachedFiles:
      visibleAttachments && visibleAttachments.length > 0
        ? visibleAttachments
        : undefined,
    meta: toUIMeta(conversation.meta),
    createdAt: undefined, // web Conversation doesn't have createdAt
    helpfulSubmitted: deriveHelpfulSubmitted(
      conversation.feedbacks,
      ctx?.viewerIdentity
    ),
    improveSubmitted: deriveImproveSubmitted(
      conversation.feedbacks,
      ctx?.viewerIdentity
    ),
  }
}

/**
 * Convert app ConversationGroup to UI ChatMessageGroup.
 * Use when passing grouped messages to @f2e/ui components.
 */
export const toUIMessageGroup = (
  group: ConversationGroup,
  ctx?: WebChatMapperContext
): ChatMessageGroup => ({
  id: group.id,
  conversations: group.conversations.map((c) => toUIMessage(c, ctx)),
  reasoningSteps: group.reasoningSteps?.map(toUIReasoningStep),
})

/**
 * Convert app History to UI ChatHistory.
 * Use when passing history/session info to @f2e/ui components.
 */
export const toUIHistory = (history: History): ChatHistory => ({
  id: history.id,
  name: history.name,
  updatedAt: history.updatedAt,
  createdAt: history.createdAt,
})

// ----- Reverse converters (UI → App) -----

/**
 * Convert UI ReasoningStep back to app ReasoningStep.
 */
const fromUIReasoningStep = (step: UIReasoningStep): ReasoningStep =>
  step as unknown as ReasoningStep

/**
 * Convert UI ChatMessageMeta back to app ConversationMeta.
 */
const fromUIMeta = (meta?: ChatMessageMeta): ConversationMeta | undefined => {
  if (!meta) return undefined
  return {
    reasoningSteps: meta.reasoningSteps?.map(fromUIReasoningStep),
    relatedQuestions: meta.relatedQuestions,
    agentProfile: meta.agentProfile
      ? {
          id: meta.agentProfile.id,
          name: meta.agentProfile.name,
          type: "copilot" as AgentType, // Default type for reverse conversion
          systemPrompt: "",
        }
      : undefined,
    assistantResponse: meta.assistantResponse,
    outputComponents: meta.outputComponents,
  }
}

/**
 * Convert UI ChatMessage back to app Conversation.
 * Use when receiving data from @f2e/ui component callbacks.
 *
 * Note: This creates a partial Conversation without app-specific fields like
 * `score`, `queries`, `fileBadges`.
 * The `feedbacks` field is not populated since helpfulSubmitted/improveSubmitted
 * are already computed fields in the UI layer.
 */
export const fromUIMessage = (message: ChatMessage): Conversation => ({
  id: message.id,
  groupId: message.groupId,
  role: message.role as Role,
  content: message.content,
  attachedFiles: message.attachedFiles,
  meta: fromUIMeta(message.meta),
  // Note: feedbacks not populated - use helpfulSubmitted/improveSubmitted directly
})

// ----- HITL Form converters -----

/**
 * Convert shared HITL FormRequestField to @f2e/ui FormRequestField.
 */
const toUIFormField = (field: HitlFormRequestField): FormRequestField => ({
  name: field.name,
  question: field.question,
  label: field.label,
  type: field.type,
  required: field.required,
  options: field.options,
  placeholder: field.placeholder,
  helpText: field.helpText,
  descriptionImage: field.descriptionImage,
  defaultValue: field.defaultValue,
})

/**
 * Convert shared HITL FormRequestSchema to @f2e/ui FormRequestSchema.
 * Use when passing HITL form data to @f2e/ui InteractionRequestForm.
 */
export const toUIFormSchema = (
  schema: HitlFormRequestSchema
): FormRequestSchema => ({
  id: schema.id,
  title: schema.title,
  description: schema.description,
  fields: schema.fields.map(toUIFormField),
  ui: schema.ui,
})
